import { t } from 'i18next'
import { atom, PrimitiveAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { DateTime } from 'luxon'

import { Text } from '@/components/ui/typography'
import { currentEnv } from '@/env/vars.ts'
import { IMimeType, IMimeTypes } from '@/types/documents/File'
import { ITextTranslate, TQuestionAction } from '@/types/gigiChat/Conversation'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'
import { IAsyncProcess } from '@/types/translation/Translate'
import { deepClone } from '@/utils'
import { DELETE, GET, POST } from '@/utils/fetch'

import { store } from '..'
import { chatSettingsAtom, conversationsListItemAtom } from '../gigiChat/ChatAtom'
import { addNotification } from '../gigiChat/Notifications'
import { getQuotas } from '../gigiChat/Quotas'
import { IGlossaryDto, IGlossaryInfo, ISupportedFromLanguage } from './dto/glossary.dto'

export const translationFileExtensionsAtom = atomWithStorage<Array<IMimeType> | null>('fileExtension', null)
export const translateLeftSideExpandedAtom: PrimitiveAtom<boolean> = atom(true)
export const translationsAtom = atom<ITextTranslate[]>([])
export const selectedLanguagesAtom = atom<ILanguage[]>([])
export const selectedGlossaryAtom = atom<ISupportedFromLanguage | undefined>(undefined)
export const showOptionsAtom = atom(false)
export const langsAtom = atom<ILanguage[] | []>([])
export const glossariesInfoAtom = atom<IGlossaryInfo[] | []>([])
export const questionActionAtom = atom<TQuestionAction | undefined>(undefined)
export const translateTextEventsAtom = atom<ITextTranslate[]>([])

export const asyncTimestampAtom = atom<string>('')

export const uploadTranslateFiles = async (formData: FormData) => {
    return await POST(`${currentEnv.chatUrl}/user/translate/docs`, formData, true).then((response) => {
        if (response && response.status === 413) {
            void getQuotas()
        }
        return response
    })
}

export const deleteTranslateFile = async (fileName: string) => await DELETE(`${currentEnv.chatUrl}/user/translate/docs/${fileName}`)

export const getAllowedTranslationFileExtensions = async () => {
    const result = await GET<IMimeTypes>(
        `${currentEnv.features.isEnabled('mimeTypesNode') ? currentEnv.coreChatUrl : currentEnv.chatUrl}/system/translate/mime_types`,
    )

    if (result && result.mimeTypes) {
        store.set(translationFileExtensionsAtom, result.mimeTypes)
    }
}

export interface ILanguage {
    languageCode: string
    languageEnglishName: string
    languageNativeName: string
    commonLanguage: boolean
}

export const toggleSelectedLanguages = (item: ILanguage) => {
    let langs = [...store.get(selectedLanguagesAtom)]
    const existingSettings = deepClone<IConversationSettings | null>(store.get(chatSettingsAtom))

    if (langs.find((lang) => lang.languageEnglishName == item.languageEnglishName)) {
        langs = langs.filter((lang) => lang.languageEnglishName !== item.languageEnglishName)
    } else {
        // no more than 10 languages selected at the same time
        if (langs.length <= 9) {
            langs.push(item)
        } else {
            addNotification('warning', t('translate.toasts.maxLangs'))
        }
    }

    if (existingSettings) {
        existingSettings.processingStrategyTranslation.languages = langs.map((lang) => lang.languageCode)
    }
    store.set(chatSettingsAtom, existingSettings)
    store.set(selectedLanguagesAtom, langs)
}

export const toggleSelectedGlossary = (item?: ISupportedFromLanguage) => {
    const existingSettings = deepClone<IConversationSettings | null>(store.get(chatSettingsAtom))

    if (existingSettings) {
        existingSettings.processingStrategyTranslation.glossary = item?.glossaryFromLanguageId ?? null
    }
    store.set(chatSettingsAtom, existingSettings)
    store.set(selectedGlossaryAtom, item)
}

export const getLanguages = async () => {
    const result = await GET<{ languages: ILanguage[] }>(`${currentEnv.chatUrl}/system/translate/languages`)

    if (result) {
        store.set(langsAtom, result.languages)
        return result.languages
    }
    return []
}

export const getGlossariesInfo = async () => {
    const result = await GET<IGlossaryDto>(`${currentEnv.utilityUrl}/glossary/info`)

    if (result) {
        store.set(glossariesInfoAtom, result.data)
        return result.data
    }
    return []
}

export const showOneDriveButton = () => {
    const conversations = store.get(conversationsListItemAtom)
    return conversations.some((item) => item.questionDisplayData?.displayData.fileNames?.length)
}

export const setQuestionActionAtom = (value: TQuestionAction) => {
    store.set(questionActionAtom, value)
}

const setCurrentTimestamp = (startT: string, endT: string) => {
    // Parse the timestamps
    const start = DateTime.fromISO(startT)
    const end = DateTime.fromISO(endT)

    // Calculate the difference in seconds
    const diffInSeconds = end.diff(start, 'seconds').seconds

    // Convert the difference to a human-readable format
    const hours = Math.floor(diffInSeconds / 3600)
    const minutes = Math.floor((diffInSeconds % 3600) / 60)
        .toString()
        .padStart(2, '0')
    const seconds = Math.floor(diffInSeconds % 60)
        .toString()
        .padStart(2, '0')

    // Format the elapsed time
    let elapsedTime
    if (hours > 0) {
        elapsedTime = `${hours.toString().padStart(2, '0')}:${minutes}:${seconds} Elapsed`
    } else {
        elapsedTime = `${minutes}:${seconds} Elapsed`
    }

    store.set(asyncTimestampAtom, elapsedTime)
}

export const getTranslateFilesStatus = async (userPromptId: number) => {
    if (userPromptId) {
        const result = await GET<IAsyncProcess>(
            `${currentEnv.features.isEnabled('gigiCore') ? currentEnv.coreChatUrl : currentEnv.chatUrl}/asyncprocess/docTranslate/status/${userPromptId}`,
        )

        if (result) {
            setCurrentTimestamp(result.askTimestamp, result.asyncProcessLastUpdateDt)
            store.set(chatSettingsAtom, result.settings)
            return result.structuredAnswer.docTranslate
        } else {
            store.set(asyncTimestampAtom, '')
        }
    }
}

export const getFullLangueName = (settings: IConversationSettings) => {
    const langs = store.get(langsAtom)

    return settings.processingStrategyTranslation.languages.map((lang: string, i: number) => {
        return (
            <Text key={lang} as="span">
                {langs.filter((language) => language.languageCode === lang)[0]?.languageEnglishName}
                {i < settings.processingStrategyTranslation.languages.length - 1 ? ', ' : ''}
            </Text>
        )
    })
}

export const getLanguageFullName = (languageCode: string): string => {
    const langs = store.get(langsAtom)
    const language = langs.find((lang) => lang.languageCode === languageCode)
    return language ? language.languageEnglishName : languageCode
}
